import cn from 'classnames'
import {FC, MutableRefObject, useEffect, useLayoutEffect, useRef} from 'react'

import {Container} from '@/components/common/content/Container'
import {COLLAPSE_TRANSITION, EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {
  CONTAINER_PADDING,
  CONTAINER_PADDING_MOBILE,
  HEADER_HEIGHT_DESKTOP,
  HEADER_MAX_WIDTH,
} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

const PANEL_OFFSET = theme.spacing[1]
const FOCUS_OUTLINE_SPACE = theme.spacing[1]

type SubmenuLayoutProps = {
  id?: string
  isVisible: boolean
  isSwitching?: boolean
  panelHeightRef?: MutableRefObject<number>
}

export const SubmenuLayout: FC<React.PropsWithChildren<SubmenuLayoutProps>> = ({
  id,
  isVisible,
  isSwitching = false,
  panelHeightRef,
  children,
}) => {
  const contentRef = useRef<HTMLDivElement>(null)

  // Keeps the shared height current while this panel is open, so the next submenu can start from it
  useEffect(() => {
    const content = contentRef.current
    if (!content || !panelHeightRef || !isVisible) {
      return
    }

    const observer = new ResizeObserver(() => (panelHeightRef.current = content.offsetHeight))
    observer.observe(content)
    return () => observer.disconnect()
  }, [isVisible, panelHeightRef])

  // Switching from another submenu resizes from its height to this one's. A layout effect, so the
  // first painted frame already has the previous height.
  useLayoutEffect(() => {
    const content = contentRef.current
    if (!content || !panelHeightRef || !isVisible || !isSwitching) {
      return
    }

    // element.animate() ignores the CSS reduced-motion rule, so check it here
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    content.animate(
      [{height: `${panelHeightRef.current}px`}, {height: `${content.offsetHeight}px`}],
      {duration: EXPAND_TRANSITION, easing: 'ease-out'},
    )
  }, [isVisible, isSwitching, panelHeightRef])

  return (
    <>
      <div id={id} className={cn('submenu', isVisible && 'open', isSwitching && 'switching')}>
        <div ref={contentRef} className="content">
          <Container>
            <div className="list">{children}</div>
          </Container>
        </div>
      </div>

      <style jsx>{`
        .submenu {
          display: none;
          opacity: 0;
          position: fixed;
          top: calc(${HEADER_HEIGHT_DESKTOP} + ${PANEL_OFFSET});
          left: 0;
          right: 0;
          width: calc(100% - ${theme.spacing[5]});
          max-width: ${HEADER_MAX_WIDTH};
          margin: 0 auto;
          padding: ${theme.spacing[6]} 0;
          background-color: ${theme.palette.tertiary.main};
          border-radius: 20px;
          box-shadow: 0 0 0 1px ${theme.palette.gray[600]};
          transition: opacity ${COLLAPSE_TRANSITION}ms ease-in,
            display ${COLLAPSE_TRANSITION}ms allow-discrete;
        }

        .submenu::before {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 100%;
          height: calc(${theme.spacing[3]} + ${PANEL_OFFSET});
        }

        .submenu.open {
          display: block;
          opacity: 1;
          animation: fade-in ${EXPAND_TRANSITION}ms ease-out;
        }

        .submenu.switching {
          transition: none;
        }

        .submenu.open.switching {
          animation: none;
        }

        .submenu.open.switching .list {
          animation: fade-in ${EXPAND_TRANSITION}ms ease-out;
        }

        .content {
          box-sizing: border-box;
          overflow: hidden;
          padding: ${FOCUS_OUTLINE_SPACE} 0;
          margin: -${FOCUS_OUTLINE_SPACE} 0;
        }

        .list {
          display: flex;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
        }

        @media ${TABLET_SCREENSIZE} {
          .submenu {
            position: relative;
            padding: 0;
            top: 0;
            left: 0;
            transform: none;
            width: 100%;
            box-shadow: none;
          }

          .submenu::before {
            display: none;
          }

          .content {
            overflow: visible;
            padding: 0;
            margin: 0;
          }

          .list {
            display: block;
            margin: 0 -${CONTAINER_PADDING};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .list {
            margin: 0 -${CONTAINER_PADDING_MOBILE};
          }
        }
      `}</style>
    </>
  )
}
